// feat_408 — SOPORTE-F4b: pestaña "Cobertura".
// Arriba: brechas de conocimiento (temas que preguntan los clientes y no están en los
// manuales), con consultas afectadas, estado y "Crear manual". Abajo (solo master): mapa de
// módulos del producto (rutas, menú y pestañas de Ajustes, leídos del código) contra las
// secciones de los manuales de cliente.
import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Chip, CircularProgress, MenuItem, Paper, TextField, Typography, makeStyles } from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import { errorText, fmtDateTime } from "./shared";
import ManualDialog, { DELIVERY_LABELS, GAP_STATUS_LABELS } from "./ManualDialog";

const useStyles = makeStyles((theme) => ({
  block: { padding: theme.spacing(1.5), marginBottom: theme.spacing(2) },
  row: {
    borderTop: "1px solid #eee",
    padding: theme.spacing(1, 0),
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    alignItems: "center",
  },
  grow: { flex: "1 1 240px", minWidth: 0 },
  chips: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: theme.spacing(1) },
}));

const COVERAGE_STYLE = {
  sin_manual: { label: "Sin manual", style: { background: "#d32f2f", color: "#fff" } },
  parcial: { label: "Parcial", style: { background: "#f57c00", color: "#fff" } },
  cubierto: { label: "Cubierto", style: { background: "#2e7d32", color: "#fff" } },
};

const CoverageTab = ({ master, onOpenRequest }) => {
  const classes = useStyles();
  const [gapStatus, setGapStatus] = useState("");
  const [gaps, setGaps] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [onlyHoles, setOnlyHoles] = useState(true);
  const [manualGap, setManualGap] = useState(null);

  const loadGaps = useCallback(() => {
    api.get("/superadmin/support/gaps", { params: { status: gapStatus || undefined } })
      .then(({ data }) => setGaps(data))
      .catch((err) => toast.error(errorText(err)));
  }, [gapStatus]);

  useEffect(() => { loadGaps(); }, [loadGaps]);
  useEffect(() => {
    if (!master) return;
    api.get("/superadmin/support/coverage").then(({ data }) => setCoverage(data)).catch((err) => toast.error(errorText(err)));
  }, [master]);

  const closeManual = useCallback(() => setManualGap(null), []);
  const rows = coverage ? coverage.rows.filter((r) => !onlyHoles || r.status !== "cubierto") : [];

  return (
    <>
      <Paper variant="outlined" className={classes.block} data-coverage-gaps>
        <Box style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <Typography variant="subtitle1" style={{ flex: 1 }}>Brechas de conocimiento</Typography>
          <TextField select size="small" variant="outlined" label="Estado" value={gapStatus} onChange={(e) => setGapStatus(e.target.value)} style={{ minWidth: 180 }}>
            <MenuItem value="">Abiertas y pedidas</MenuItem>
            {Object.entries(GAP_STATUS_LABELS).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
            <MenuItem value="todas">Todas</MenuItem>
          </TextField>
        </Box>
        {gaps && (
          <div className={classes.chips}>
            {Object.entries(GAP_STATUS_LABELS).map(([k, l]) => (
              <Chip key={k} size="small" variant="outlined" label={`${l}: ${gaps.byStatus?.[k] || 0}`} />
            ))}
          </div>
        )}
        {!gaps ? <CircularProgress size={24} /> : gaps.gaps.length === 0 ? (
          <Typography color="textSecondary">No hay brechas para este filtro.</Typography>
        ) : gaps.gaps.map((g) => (
          <div key={g.id} className={classes.row} data-gap={g.id}>
            <div className={classes.grow}>
              <Typography variant="body2"><strong>#{g.id} · {g.topic}</strong></Typography>
              <Typography variant="caption" color="textSecondary" component="div">{g.description}</Typography>
              {g.probableModules?.length > 0 && (
                <Typography variant="caption" color="textSecondary" component="div">Dónde mirar: {g.probableModules.join(" · ")}</Typography>
              )}
              <Box style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
                {g.requestIds.slice(0, 8).map((id) => (
                  <Chip key={id} size="small" clickable variant="outlined" label={`#${id}`} onClick={() => onOpenRequest(id)} />
                ))}
              </Box>
            </div>
            <Chip size="small" variant="outlined" label={`${g.affectedCount} consulta(s)`} />
            <Chip size="small" label={GAP_STATUS_LABELS[g.status] || g.status} />
            {g.status === "manual_en_cola" && (
              <Chip size="small" variant="outlined" label={`${DELIVERY_LABELS[g.delivery] || g.delivery}${g.queuedByName ? ` · ${g.queuedByName}` : ""}${g.queuedAt ? ` · ${fmtDateTime(g.queuedAt)}` : ""}`} />
            )}
            <Button size="small" variant={g.status === "abierta" ? "contained" : "outlined"} color="primary" onClick={() => setManualGap(g.id)} data-gap-manual={g.id}>
              {g.status === "abierta" ? "Crear manual" : "Ver prompt"}
            </Button>
          </div>
        ))}
      </Paper>

      {master && (
        <Paper variant="outlined" className={classes.block} data-coverage-map>
          <Typography variant="subtitle1">Mapa de cobertura</Typography>
          {!coverage ? <CircularProgress size={24} /> : (
            <>
              <Typography variant="caption" color="textSecondary" component="div" style={{ marginBottom: 8 }}>
                Pantallas que ve un cliente (rutas, menú y pestañas de Ajustes, leídas del código el {fmtDateTime(coverage.inventoryGeneratedAt)})
                contra {coverage.manuals.sections} secciones de {coverage.manuals.files.join(", ")}. «Cubierto» = tiene sección propia; «Parcial» = se
                menciona pero sin sección; las {coverage.totals.superadminExcluded} pantallas de superadmin no cuentan.
              </Typography>
              <div className={classes.chips}>
                <Chip size="small" style={COVERAGE_STYLE.sin_manual.style} label={`Sin manual: ${coverage.totals.sin_manual}`} />
                <Chip size="small" style={COVERAGE_STYLE.parcial.style} label={`Parcial: ${coverage.totals.parcial}`} />
                <Chip size="small" style={COVERAGE_STYLE.cubierto.style} label={`Cubierto: ${coverage.totals.cubierto}`} />
                <Chip size="small" clickable color={onlyHoles ? "primary" : "default"} label={onlyHoles ? "Mostrando solo huecos" : "Mostrando todo"} onClick={() => setOnlyHoles((v) => !v)} />
              </div>
              {rows.length === 0 ? <Typography color="textSecondary">Sin huecos: todas las pantallas tienen sección propia.</Typography> : rows.map((r) => (
                <div key={r.path} className={classes.row} data-coverage-row={r.status}>
                  <div className={classes.grow}>
                    <Typography variant="body2"><strong>{r.name}</strong> <Typography component="span" variant="caption" color="textSecondary">{r.path}</Typography></Typography>
                    {r.section ? (
                      <Typography variant="caption" color="textSecondary" component="div">{r.section.file} › {r.section.path}</Typography>
                    ) : (
                      <Typography variant="caption" color="textSecondary" component="div">
                        {r.mentions ? `Mencionado en ${r.mentions} sección(es), sin sección propia.` : "No aparece en los manuales de cliente."}
                      </Typography>
                    )}
                    {r.relatedFeatures?.length > 0 && (
                      <Typography variant="caption" color="textSecondary" component="div">Features: {r.relatedFeatures.join(" · ")}</Typography>
                    )}
                  </div>
                  <Chip size="small" style={COVERAGE_STYLE[r.status].style} label={COVERAGE_STYLE[r.status].label} />
                </div>
              ))}
            </>
          )}
        </Paper>
      )}

      {manualGap && <ManualDialog gapId={manualGap} open onClose={closeManual} onDone={loadGaps} />}
    </>
  );
};

export default CoverageTab;
